import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Typography } from '@/components/ui/typography'
import { Image as ImageIcon, LockKeyhole, RotateCw, Undo2 } from 'lucide-react'

const layoutLabels = {
  classic: 'Classic',
  split: 'Split',
  professional: 'Professional',
  inherit: 'Keep florist style',
}

export default function SeasonalSuggestionControls({
  hero,
  seasonal,
  hasUndo,
  health,
  isPending,
  onKeepChange,
  onShuffle,
  onUndo,
}) {
  const isCustomized = seasonal?.hero_mode === 'local'
  const heroProps = hero?.props ?? {}
  const heroMedia = hero?.media?.[0] ?? null
  const focalPointX = heroProps.imageFocalPointX ?? heroMedia?.focal_point_x ?? 50
  const focalPointY = heroProps.imageFocalPointY ?? heroMedia?.focal_point_y ?? 50
  const lookName =
    seasonal?.seasonal_design?.name || (isCustomized ? 'Your customized look' : 'Seasonal banner')

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/20 px-3 py-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Typography variant="label" className="truncate">
              {lookName}
            </Typography>
            <Badge variant="outline">{isCustomized ? 'Customized' : 'Automatic'}</Badge>
          </div>
          <div className="flex items-center gap-1">
            {hasUndo ? (
              <Button type="button" size="sm" variant="ghost" className="h-8" onClick={onUndo}>
                <Undo2 className="size-3.5" /> Undo
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8"
              disabled={isPending}
              onClick={onShuffle}
            >
              <RotateCw className="size-3.5" />
              {isPending ? 'Finding a look...' : 'Shuffle look'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-3 p-3">
          <div className="grid aspect-[4/3] place-items-center overflow-hidden rounded-lg bg-muted text-muted-foreground">
            {heroMedia?.url ? (
              <img
                src={heroMedia.url}
                alt=""
                className="size-full object-cover"
                style={{ objectPosition: `${focalPointX}% ${focalPointY}%` }}
              />
            ) : (
              <ImageIcon className="size-5" />
            )}
          </div>
          <dl className="grid min-w-0 content-center grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
            <dt className="font-medium text-muted-foreground">Style</dt>
            <dd className="truncate">{layoutLabels[heroProps.layout] || 'Classic'}</dd>
            <dt className="font-medium text-muted-foreground">Headline</dt>
            <dd className="truncate">{heroProps.title || 'None'}</dd>
            <dt className="font-medium text-muted-foreground">Button</dt>
            <dd className="truncate">{heroProps.buttonText || 'None'}</dd>
          </dl>
        </div>
      </div>

      <label className="flex min-h-11 items-center justify-between gap-4 rounded-xl border bg-muted/15 px-3 py-2">
        <span>
          <span className="flex items-center gap-1.5 text-sm font-medium">
            <LockKeyhole className="size-3.5 text-muted-foreground" aria-hidden="true" />
            Keep this look every year
          </span>
          <span className="block text-xs text-muted-foreground">
            Leave this off to receive a different curated look after this season ends.
          </span>
        </span>
        <Switch
          checked={!!seasonal?.keep_for_future_years}
          onCheckedChange={onKeepChange}
          aria-label="Keep this look every year"
        />
      </label>

      {health?.messages?.length ? (
        <div className="rounded-lg border px-3 py-2 text-sm text-muted-foreground">
          {health.messages.map((message) => (
            <div key={message}>{message}</div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
